import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { FieldArray, Form, Formik, type FormikHelpers } from 'formik';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import api from 'api';
import { EARLIEST_BEGIN_DATE } from 'data/constants/constants';
import { LICENSE_COUNTRIES, MAX_LICENSES } from 'helpers/license';
import { useUpdateLicenses } from 'hooks/useLicenses';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { ConsultantLicenseFormInterface, ConsultantLicenseInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    emptyConsultantLicense,
    licenseFormDefaultValues,
    licenseFormValidationSchema,
} from 'utils/validations/license';

interface LicenseEntriesFormProps {
    initialLicenses: ConsultantLicenseInterface[];
}

export const LicenseEntriesForm: React.FC<LicenseEntriesFormProps> = ({ initialLicenses }) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const updateLicenses = useUpdateLicenses();

    const knownLicenseNumbers = useMemo(
        () => initialLicenses.map((item) => item.license_number.trim()).filter(Boolean),
        [initialLicenses]
    );

    const countryPool = useMemo(() => {
        const extras = initialLicenses
            .map((item) => item.country.trim())
            .filter((country) => country && !LICENSE_COUNTRIES.includes(country));
        return [...LICENSE_COUNTRIES, ...new Set(extras)];
    }, [initialLicenses]);

    const handleSubmit = async (
        values: ConsultantLicenseFormInterface,
        helpers: FormikHelpers<ConsultantLicenseFormInterface>
    ) => {
        const known = new Set(knownLicenseNumbers.map((number) => number.toLowerCase()));

        try {
            for (let index = 0; index < values.licenses.length; index += 1) {
                const licenseNumber = values.licenses[index].license_number.trim();
                if (!licenseNumber || known.has(licenseNumber.toLowerCase())) continue;

                try {
                    await api.immigrationProfessional.checkLicense(licenseNumber);
                } catch (error) {
                    helpers.setFieldTouched(`licenses.${index}.license_number`, true, false);
                    helpers.setFieldError(
                        `licenses.${index}.license_number`,
                        getErrorMessage(error, translate('licenseAlreadyRegistered'))
                    );
                    return;
                }
            }

            await updateLicenses.mutateAsync(values.licenses);
            dispatch(setAlert({ message: translate('licensesSaved'), type: 'success' }));
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={licenseFormDefaultValues(initialLicenses)}
            validationSchema={licenseFormValidationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, isSubmitting }) => {
                const canAddLicense =
                    values.licenses.length < Math.min(MAX_LICENSES, countryPool.length);

                return (
                    <Form>
                        <FieldArray name="licenses">
                            {({ push, remove }) => (
                                <Stack spacing={2}>
                                    {values.licenses.map((license, index) => {
                                        const takenCountries = new Set(
                                            values.licenses
                                                .map((item, itemIndex) =>
                                                    itemIndex === index ? '' : item.country.trim()
                                                )
                                                .filter(Boolean)
                                        );
                                        const countryOptions = countryPool
                                            .filter(
                                                (country) =>
                                                    country === license.country ||
                                                    !takenCountries.has(country)
                                            )
                                            .map((country) => ({
                                                label: country,
                                                value: country,
                                            }));

                                        return (
                                            <Box
                                                key={`license-${license.id ?? index}`}
                                                sx={{
                                                    pb: 1,
                                                    borderBottom:
                                                        index < values.licenses.length - 1
                                                            ? '1px solid'
                                                            : 'none',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                {values.licenses.length > 1 ? (
                                                    <Stack
                                                        direction="row"
                                                        sx={{
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            mb: 1.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            {translate('licenseItem')} {index + 1}
                                                        </Typography>
                                                        <IconButton
                                                            type="button"
                                                            aria-label={translate('removeLicense')}
                                                            color="error"
                                                            size="small"
                                                            disabled={isSubmitting}
                                                            onClick={() => remove(index)}
                                                        >
                                                            <DeleteOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                ) : null}
                                                <FormGridLayout
                                                    sx={{
                                                        gridTemplateColumns: {
                                                            xs: '1fr',
                                                            sm: '1fr 1fr',
                                                            md: '1fr 1fr 1fr',
                                                        },
                                                    }}
                                                >
                                                    <FormField
                                                        name={`licenses.${index}.country`}
                                                        label="country"
                                                        type="select"
                                                        options={countryOptions}
                                                        required
                                                        disabled={isSubmitting}
                                                    />
                                                    <FormField
                                                        name={`licenses.${index}.license_number`}
                                                        label="consultantLicenseNumber"
                                                        required
                                                        disabled={isSubmitting}
                                                    />
                                                    <FormField
                                                        name={`licenses.${index}.date_issued`}
                                                        label="dateIssued"
                                                        type="date"
                                                        required
                                                        minDate={EARLIEST_BEGIN_DATE}
                                                        maxDate={new Date()}
                                                        disabled={isSubmitting}
                                                    />
                                                </FormGridLayout>
                                            </Box>
                                        );
                                    })}

                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                        sx={{ justifyContent: 'flex-end' }}
                                    >
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            startIcon={<AddOutlined />}
                                            disabled={isSubmitting || !canAddLicense}
                                            onClick={() => push({ ...emptyConsultantLicense })}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {translate('addCountry')}
                                        </Button>
                                        <SubmitButton loading={isSubmitting}>
                                            saveChanges
                                        </SubmitButton>
                                    </Stack>
                                </Stack>
                            )}
                        </FieldArray>
                    </Form>
                );
            }}
        </Formik>
    );
};
